import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../services/auth.service';
import {
  CreateFieldPayload,
  CreateFieldResponse,
  CreateLookupFieldsPayload,
  CreateSummaryFieldPayload,
  DependentFieldsSuccess,
  Field,
  FieldDependencies,
  FieldType,
  UpdateFieldPayload,
} from '../_models/field.model';
import { FormField } from '../_models/form.model';

@Injectable({
  providedIn: 'root',
})
export class FieldService {
  private baseURL = `${environment.apiUrl}/api`;
  private midURL = '/field';
  private SUMMARY_FORM_FIELD_URL = '/getsummaryformfields';
  private TABLE_FIELD_URL = '/getbytableid?Id=';
  private FIELD_TYPE_URL = '/getfieldtypes';
  private LOOKUP_FIELDS_URL = '/lookupablefields?tableId=';
  private SUMMARIZABLE_FIELDS_URL = '/summarizablefields?tableId=';
  private SUMMARY_FIELDS_URL = '/SummaryFields?tableId=';
  private EXECUTE_FORMULA_URL = '/executeformula';
  private RUN_TODAY_FORMULAS_URL = '/runtodayformulas?sync=false';
  private VISUALIZATION_DATA = '/getvisualiationdata';
  private DEPENDENT_FIELDS = '/getdependentfields';
  private CREATE_MULTIPLE_FIELDS = '/createmultiplefields';
  private readonly CREATE_LOOKUP_FIELDS = '/createlookups';
  private UPDATE_LOOKUP_FIELD = '/updatelookupsdisplayname';

  public get getTableFieldUrl() {
    return `${this.baseURL}${this.midURL}${this.TABLE_FIELD_URL}`;
  }

  public get getFieldTypesUrl() {
    return `${this.baseURL}${this.midURL}${this.FIELD_TYPE_URL}`;
  }

  public get getCreateFieldUrl() {
    return `${this.baseURL}${this.midURL}`;
  }

  public get getCreateLookupFieldsUrl() {
    return `${this.baseURL}${this.midURL}${this.CREATE_LOOKUP_FIELDS}`;
  }

  public get getLookupFieldUrl() {
    return `${this.baseURL}${this.midURL}${this.LOOKUP_FIELDS_URL}`;
  }

  public get getSummaryFormFieldUrl() {
    return `${this.baseURL}${this.midURL}${this.SUMMARY_FORM_FIELD_URL}`;
  }

  public get updateLooupFieldUrl() {
    return `${this.baseURL}${this.midURL}${this.UPDATE_LOOKUP_FIELD}`;
  }

  public get getSummarizableFieldUrl() {
    return `${this.baseURL}${this.midURL}${this.SUMMARIZABLE_FIELDS_URL}`;
  }

  public get getSummaryFieldUrl() {
    return `${this.baseURL}${this.midURL}${this.SUMMARY_FIELDS_URL}`;
  }

  public get getExecuteFormulaUrl() {
    return `${this.baseURL}${this.midURL}${this.EXECUTE_FORMULA_URL}`;
  }

  public get getRunTodayFormulasUrl() {
    return `${this.baseURL}${this.midURL}${this.RUN_TODAY_FORMULAS_URL}`;
  }

  public get getVisualizationDataUrl() {
    return `${this.baseURL}${this.midURL}${this.VISUALIZATION_DATA}`;
  }

  public get getDependentFieldsUrl() {
    return `${this.baseURL}${this.midURL}${this.DEPENDENT_FIELDS}`;
  }

  public get getCreateMultipleFields() {
    return `${this.baseURL}${this.midURL}${this.CREATE_MULTIPLE_FIELDS}`;
  }


  constructor(private http: HttpClient) {}

  /**
   * Get fields
   */
  getFields = (tableId: string): Observable<Array<Field>> =>
    this.http.get<Array<Field>>(`${this.getTableFieldUrl}${tableId}`, {});

  /**
   * Get field types
   */
  getFieldTypes = (): Observable<Array<FieldType>> =>
    this.http.get<Array<FieldType>>(`${this.getFieldTypesUrl}`, {});

  /**
   * create field
   */
  createField = (
    payload: CreateFieldPayload | CreateSummaryFieldPayload
  ): Observable<CreateFieldResponse> =>
    this.http.post<CreateFieldResponse>(
      `${this.getCreateFieldUrl}`,
      payload,
      {}
    );

  /**
   * create multiple field
   */
  createMultipleFields = (
    payload: Array<CreateFieldPayload> | Array<CreateSummaryFieldPayload>
  ): Observable<Array<CreateFieldResponse>> =>
    this.http.post<Array<CreateFieldResponse>>(
      `${this.getCreateMultipleFields}`,
      payload,
      {}
    );

  /**
   * Get field types
   */
  deleteField = (fieldId: string): Observable<void> =>
    this.http.delete<void>(`${this.getCreateFieldUrl}?Id=${fieldId}`, {});

  /**
   * Get single field
   */
  getField = (fieldId: string): Observable<Field> =>
    this.http.get<Field>(`${this.getCreateFieldUrl}?Id=${fieldId}`, {});

  /**
   * Get single field
   */
  getSummaryFormField = (tableId:any,fieldId: string): Observable<Array<FormField>> =>
    this.http.get<Array<FormField>>(`${this.getSummaryFormFieldUrl}?tableId=${tableId}&id=${fieldId}`, {});

  /**
   * update single field
   */
  updateField = (
    payload: UpdateFieldPayload | CreateSummaryFieldPayload
  ): Observable<void> =>
    this.http.put<void>(`${this.getCreateFieldUrl}`, payload, {});
  /**
   * update lookup field
   */
  updateLookupField = (
    payload: UpdateFieldPayload | CreateSummaryFieldPayload
  ): Observable<void> =>
    this.http.put<void>(`${this.updateLooupFieldUrl}`, payload, {});

  /**
   * Get lookup field
   */
  getLookupFields = (tableId: string): Observable<Array<Field>> =>
    this.http.get<Array<Field>>(`${this.getLookupFieldUrl}${tableId}`, {});

  /**
   * Get summarizable fields
   */
  getSummarizableFields = (tableId: string): Observable<Array<Field>> =>
    this.http.get<Array<Field>>(
      `${this.getSummarizableFieldUrl}${tableId}`,
      {}
    );

  /**
   * Get summary fields
   */
  getSummaryFields = (
    fromTableId: string,
    toTableId: string
  ): Observable<Array<Field>> =>
    this.http.get<Array<Field>>(
      `${this.getSummaryFieldUrl}${fromTableId}&table2Id=${toTableId}`,
      {}
    );

  /**
   * execute formula
   */
  executeFormula = (tableId: string, fieldKey: string): Observable<null> =>
    this.http.post<null>(
      `${this.getExecuteFormulaUrl}`,
      {
        ApplicationTableKey: tableId,
        Key: fieldKey,
      },
      {}
    );

  /**
   * execute today formulas
   */
  executeTodayFormulas = (): Observable<null> =>
    this.http.get<null>(`${this.getRunTodayFormulasUrl}`);

  /**
   * Get field dependencies visualization data
   */
  getFieldDependenciesData = (
    tableId: string,
    fieldId: string
  ): Observable<FieldDependencies> =>
    this.http.get<FieldDependencies>(
      `${this.getVisualizationDataUrl}?tableId=${tableId}&fieldId=${fieldId}`,
      {}
    );

  /**
   * Get dependent fields
   */
  getDependentFields = (
    tableId: string,
    fieldId: string
  ): Observable<DependentFieldsSuccess> =>
    this.http.get<DependentFieldsSuccess>(
      `${this.getDependentFieldsUrl}?id=${fieldId}`,
      {}
    );

  /**
   * create field
   */
  createLookupFields = (
    payload: CreateLookupFieldsPayload
  ): Observable<any> =>
    this.http.post<any>(
      `${this.getCreateLookupFieldsUrl}`,
      payload,
      {}
    );
}
